import { useCallback, useEffect, useState } from 'react';
import {
  BUDGET_ROUNDS_CHANGED_EVENT,
  pickDefaultBudgetKindCode,
  refreshBudgetKindOptions,
} from 'budget/budgetRoundStore';

/**
 * 예산차수(결합코드) select — API 캐시를 채운 뒤 옵션을 반환한다.
 * autoSelect: 빈 값이면 본예산(100) 또는 첫 차수를 넣는다. false면 전체(빈 값) 유지.
 */
export function useBudgetKindOptions({
  tenantCode,
  fiscalYear,
  getValues,
  setValue,
  autoSelect = true,
} = {}) {
  const [kindOptions, setKindOptions] = useState([]);

  const syncKindOptions = useCallback(async () => {
    const fy = String(fiscalYear || getValues?.('fiscalYear') || '').trim();
    const tc = String(getValues?.('tenantCode') || tenantCode || '').trim();
    if (!tc || !fy) {
      setKindOptions([]);
      return [];
    }
    const opts = await refreshBudgetKindOptions(tc, fy);
    setKindOptions(opts);
    const current = String(getValues?.('budgetKindCode') || '').trim();
    if (!autoSelect && !current) return opts;
    const next = pickDefaultBudgetKindCode(opts, current);
    if (next !== current) {
      setValue?.('budgetKindCode', next, { shouldDirty: false });
    }
    return opts;
  }, [fiscalYear, tenantCode, getValues, setValue, autoSelect]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await syncKindOptions();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [syncKindOptions]);

  useEffect(() => {
    const onChanged = (e) => {
      const d = e.detail || {};
      const fy = String(fiscalYear || getValues?.('fiscalYear') || '').trim();
      const tc = String(getValues?.('tenantCode') || tenantCode || '').trim();
      if (d.tenantCode && d.tenantCode !== tc) return;
      if (d.fiscalYear && String(d.fiscalYear) !== fy) return;
      syncKindOptions();
    };
    window.addEventListener(BUDGET_ROUNDS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(BUDGET_ROUNDS_CHANGED_EVENT, onChanged);
  }, [syncKindOptions, fiscalYear, tenantCode, getValues]);

  return { kindOptions, syncKindOptions };
}

export default useBudgetKindOptions;
